import {useId, forwardRef, Ref, ChangeEvent} from 'react';
import './flat-color-selector.scss';
import {Autocomplete, Box, FormControl, Slide, SxProps, Typography, useTheme} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {Pallete} from '../../theme/bluebird';
import FlatInput from '../flat-input/flat-input';

export interface Option {
  key: string;
  label: string;
}

export interface FlatColorSelectorProps {
  id?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  errors?: string;
  options: Option[];
  value?: Option;
  onChange: (selectedOptions: Option) => void;
  onBlur?: () => void;
  sx?: SxProps<Theme>;
}

export const FlatColorSelector = forwardRef(
  (
    {
      label,
      required,
      errors,
      options,
      sx,
      value,
      disabledTooltip,
      onChange,
      ...rest
    }: FlatColorSelectorProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const theme = useTheme();

    const hasError = !!errors;

    const id = useId();

    const uniqueId = rest.id ?? `flat_color_select_${id}`;

    const handleChange = (_event: ChangeEvent<{}>, selectedOption: Option) =>
      onChange(selectedOption);

    const COLOR_GRID_COLUMNS = options?.length / (options.length > 16 ? 4 : 2);

    const colorBoxSx: SxProps<Theme> = {
      height: '18px',
      width: '18px',
      borderRadius: '4px',
      border: '1px solid #e4e3e3',
    };

    const colorListBoxSx: SxProps<Theme> = {
      width: `calc(${COLOR_GRID_COLUMNS} * 34px)`,
      position: 'absolute',
      right: 0,
      padding: 0,
      display: 'grid',
      gridAutoFlow: 'column',
      gridTemplateRows: options.length > 16 ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
      gridTemplateColumns: `repeat(${COLOR_GRID_COLUMNS}, 1fr)`,
      gap: 0,
      background: '#FFFFFF',
      boxShadow: '0px 1px 2px rgba(60, 64, 67, 0.3), 0px 1px 3px 1px rgba(60, 64, 67, 0.15)',
      borderRadius: '4px',
    };

    return (
      <FormControl className="flat-color-select" sx={{width: '100%'}}>
        {label && (
          <Box className="label-container">
            <Typography
              component="label"
              htmlFor={uniqueId}
              noWrap
              style={{color: rest.disabled ? 'var(--dcm-gray-disable)' : undefined}}
            >
              {label}
            </Typography>
            {required && !rest.disabled && <Typography component="span">*</Typography>}
          </Box>
        )}
        <Autocomplete
          ref={ref}
          {...rest}
          value={value}
          onChange={handleChange}
          disableClearable
          options={options}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          getOptionLabel={(option) => option.label}
          renderOption={(props, {key: color}, state) => {
            return (
              <li
                {...props}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  height: '32px',
                  width: '34px',
                  backgroundColor: state.selected ? theme.palette.primary.light : '',
                  margin: 0,
                  padding: '10px 8px',
                }}
              >
                <Box
                  sx={{
                    ...colorBoxSx,
                    backgroundColor: color,
                  }}
                />
              </li>
            );
          }}
          renderInput={(params) => (
            <Box sx={{position: 'relative'}}>
              <FlatInput
                {...params}
                ref={params.InputProps.ref}
                InputLabelProps={{children: null}}
                disabledTooltip={disabledTooltip}
                sx={{
                  '.MuiFilledInput-root': {
                    paddingTop: 0,
                  },
                  input: {
                    opacity: '0!important',
                  },
                }}
              />
              {params?.inputProps?.value && (
                <Box
                  sx={{
                    ...colorBoxSx,
                    backgroundColor: value?.key || 'transparent',
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    width: 'calc(100% - 50px)',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          )}
          ListboxProps={{
            sx: colorListBoxSx,
          }}
          sx={{
            borderRadius: '5px 5px 0 0',
            padding: 0,
            ...sx,
            '&.MuiExpanded': {
              border: '1px solid orange',
            },
            '& .MuiOutlinedInput-root': {
              padding: '2px 6px',
            },
          }}
        />
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);
